import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';
import { CARDIO_INTENSITY_LEVELS } from './constants/blockTypes';
import { getLastWorkoutForExercise } from './services/historyService';
import ExerciseHistoryModal from './components/ExerciseHistoryModal';
import IsometricInput from './components/logging/IsometricInput';
import { checkForPR, getRepRangeCategory } from './services/prService';
import { calculateOneRM, getBestEstimated1RM } from './services/oneRmService';
import { queueLog, getPendingLogs } from './utils/offlineQueue';
import {
  getOfflineSessionId,
  isOfflineSessionId,
  liveSessionId,
  onSessionReconciled,
  syncOfflineWorkout,
  hasPendingOfflineSessions,
  withTimeLimit,
} from './services/offlineSessionService';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { loadSessionSwap } from './services/swapService';
import { recordWeighIn } from './services/weightService';
import { trainingDay } from './utils/trainingDay';
import { RESUMABLE_SESSION_WINDOW_MS } from './services/sessionAccountingService';
import ExerciseSwapModal from './components/ExerciseSwapModal';
import RestTimer from './components/ui/RestTimer';
import ExerciseDemo from './components/ui/ExerciseDemo';
import { colors as theme } from './theme';

// ---------------------------------------------------------------------------
// Module-level promise cache — prevents concurrent workout_sessions INSERTs
// when multiple WorkoutLogger instances mount simultaneously for the same day.
// All instances that call getOrCreateSession with the same key will share the
// single in-flight promise, so only ONE INSERT is ever attempted.
// ---------------------------------------------------------------------------
const _sessionCache = new Map(); // key → Promise<{ sessionId, alreadyCompleted }>

// How stale an open (never-completed) session can be before it's treated as
// abandoned rather than "resume in progress". A real workout is a single sitting
// — a pause for a call or a set of stairs is fine, coming back the next day is a
// new session. Anything longer just makes a later real workout reuse a stale
// started_at and report a multi-hour/multi-day duration on the summary card.
// (Was 48h; the coach "Abandoned Sessions" list still uses 48h for its own,
// looser "hasn't been touched in ages" meaning — different question.)
// The value is RESUMABLE_SESSION_WINDOW_MS, shared with sessionAccountingService so that "too old to
// resume" here and "opened but never trained" at the end of the week are the same line.

const _resolveSession = async (userId, programId, dayId, assignedAt) => {
  // 1. Look for an existing open session, recent enough to still be a genuine resume
  const staleCutoff = new Date(Date.now() - RESUMABLE_SESSION_WINDOW_MS).toISOString();
  let q = supabase
    .from('workout_sessions')
    .select('id')
    .eq('user_id', userId)
    .eq('program_id', programId)
    .eq('day_id', dayId)
    .is('completed_at', null)
    .gte('started_at', staleCutoff);
  if (assignedAt) q = q.gte('started_at', assignedAt);
  const { data: existing } = await q.order('started_at', { ascending: false }).limit(1);
  if (existing?.[0]) return { sessionId: existing[0].id, alreadyCompleted: false };

  // 2. Check if the day was already completed
  let cq = supabase
    .from('workout_sessions')
    .select('id')
    .eq('user_id', userId)
    .eq('program_id', programId)
    .eq('day_id', dayId)
    .not('completed_at', 'is', null);
  if (assignedAt) cq = cq.gte('started_at', assignedAt);
  const { data: completed } = await cq.limit(1);
  if (completed?.[0]) return { sessionId: null, alreadyCompleted: true };

  // 3. Create a new session
  const { data: newSession, error: createError } = await supabase
    .from('workout_sessions')
    .insert({ user_id: userId, program_id: programId, day_id: dayId })
    .select('id')
    .single();

  if (!createError) return { sessionId: newSession.id, alreadyCompleted: false };

  // 4. Unique violation from an external race — re-fetch the winner
  if (createError.code === '23505') {
    let rq = supabase
      .from('workout_sessions')
      .select('id')
      .eq('user_id', userId)
      .eq('program_id', programId)
      .eq('day_id', dayId)
      .is('completed_at', null);
    if (assignedAt) rq = rq.gte('started_at', assignedAt);
    const { data: retry } = await rq.order('started_at', { ascending: false }).limit(1);
    if (retry?.[0]) return { sessionId: retry[0].id, alreadyCompleted: false };
  }

  throw createError;
};

// localStorage key used to cache session IDs so they survive page refreshes during offline use
const _sessionLsKey = (userId, programId, dayId) => `atone_session:${userId}:${programId}:${dayId}`;

// Persist the resolved session id together with the time it was resolved, so the
// offline fallback below can tell a genuinely-in-progress session from a stale
// one left over from a previous training day (which would otherwise be reused and
// report a multi-day duration).
const _writeSessionLs = (userId, programId, dayId, sessionId) => {
  try {
    localStorage.setItem(
      _sessionLsKey(userId, programId, dayId),
      JSON.stringify({ id: sessionId, ts: Date.now() })
    );
  } catch {}
};

// Returns the cached session id only if it's recent enough to still be the
// current workout. Accepts the legacy bare-string format (no timestamp) since
// that entry is rewritten in the new shape on the next successful resolve.
const _readFreshSessionLs = (userId, programId, dayId) => {
  const raw = localStorage.getItem(_sessionLsKey(userId, programId, dayId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.id) {
      return (Date.now() - parsed.ts) <= RESUMABLE_SESSION_WINDOW_MS ? parsed.id : null;
    }
  } catch {
    // legacy bare string
    return raw;
  }
  return raw;
};

const getOrCreateSession = (userId, programId, dayId, assignedAt) => {
  const key = `${userId}:${programId}:${dayId}`;
  if (_sessionCache.has(key)) return _sessionCache.get(key);
  const promise = _resolveSession(userId, programId, dayId, assignedAt)
    .then(result => {
      // Persist session ID so it can be recovered after a page refresh while offline
      if (result.sessionId) {
        _writeSessionLs(userId, programId, dayId, result.sessionId);
      }
      return result;
    })
    .catch(err => {
      // Network failure — fall back to the last known session ID from localStorage,
      // but only if it's recent enough to still be this workout.
      const isNetErr = !navigator.onLine || err?.name === 'TypeError' ||
        err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError');
      if (isNetErr) {
        const cached = _readFreshSessionLs(userId, programId, dayId);
        if (cached) return { sessionId: cached, alreadyCompleted: false };
      }
      throw err;
    })
    // Only its own entry: a lookup that was given up on may settle after a newer one has
    // taken its place.
    .finally(() => { if (_sessionCache.get(key) === promise) _sessionCache.delete(key); });
  _sessionCache.set(key, promise);
  return promise;
};

// Waits for a session lookup, but not for ever. One that gave no answer in time is dropped from
// the cache, so the next caller asks afresh instead of joining a wait that can last minutes
// (with an expired login the client spends about 25 seconds on each of the three requests).
const _awaitSession = (lookup, userId, programId, dayId) =>
  withTimeLimit(lookup).catch((err) => {
    const key = `${userId}:${programId}:${dayId}`;
    if (err?.timedOut && _sessionCache.get(key) === lookup) _sessionCache.delete(key);
    throw err;
  });

// Reconnect: give offline-started days their real session and upload the queued sets. The session
// is resolved through getOrCreateSession, so it shares one in-flight lookup with any logger that
// is mounting at the same moment instead of racing it to create the row.
const _syncOfflineWorkout = () => syncOfflineWorkout({
  resolveSession: (userId, programId, dayId, assignedAt) =>
    _awaitSession(getOrCreateSession(userId, programId, dayId, assignedAt), userId, programId, dayId),
});

// Best effort, and never waited on: `ready` does not resolve on a page with no service worker,
// and a save must not hang on it.
const _requestBackgroundSync = () => {
  if (!('serviceWorker' in navigator) || !('SyncManager' in window)) return;
  navigator.serviceWorker.ready
    .then(reg => reg.sync.register('sync-workout-logs'))
    .catch(() => {});
};
// ---------------------------------------------------------------------------

const brandColors = {
  gold: theme.gold,
  darkGray: theme.bg,
  mediumGray: theme.surface1,
  lightGray: theme.surface3,
  silver: theme.textMuted,
  white: theme.text,
};

const WorkoutLogger = ({
  programExercise,
  dayId,
  programId,
  userId,
  assignedAt,
  exerciseStatus,
  onStatusChange,
  onSetCompleted,
  onSessionCreated,
  skippedReasons = [],
  isLocked = false,
  preloadedOverride = undefined,
  preloadedFlag = undefined,
  coachResolvedNote = null,
}) => {
  const [sets, setSets] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [lastWorkout, setLastWorkout] = useState(null);
  // Best estimated 1RM over the last 90 days (highest e1RM across all logged
  // sets), not just the most recent session — a deload/back-off session must
  // not drag the displayed "Est. 1RM" below weights the user routinely lifts.
  const [bestE1RM, setBestE1RM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayAlreadyCompleted, setDayAlreadyCompleted] = useState(false);
  const [gifExpanded, setGifExpanded] = useState(false);
  const [weightHelpOpen, setWeightHelpOpen] = useState(false);
  const [failedDemoUrl, setFailedDemoUrl] = useState(null); // the demo that would not load
  const [showSkipReasons, setShowSkipReasons] = useState(false);
  const [skipReasonText, setSkipReasonText] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [prInfo, setPRInfo] = useState({}); // Track PR status per set (e.g., {0: {isPR: true, improvement: 5}})
  const [saveError, setSaveError] = useState(null);
  const [savedSet, setSavedSet] = useState(null);
  const saveFeedbackRevision = useRef(0);
  useEffect(() => {
    if (savedSet === null) return;
    const timer = setTimeout(() => setSavedSet(null), 3000);
    return () => clearTimeout(timer);
  }, [savedSet]);
  const [exerciseOverride, setExerciseOverride] = useState(null);
  const [overrideNote, setOverrideNote] = useState(null);
  const [sessionSwap, setSessionSwap] = useState(() => {
    // Read from localStorage on first render so the swap is visible instantly —
    // no async gap, no flash back to the original exercise on app resume.
    if (!userId || !programExercise?.id) return null;
    try {
      const key = `atone_swap:${userId}:${programExercise.id}:${trainingDay()}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  // Persist swap to localStorage so it survives app resume without an async gap.
  const syncSwapToStorage = (swap) => {
    if (!userId || !programExercise?.id) return;
    const key = `atone_swap:${userId}:${programExercise.id}:${trainingDay()}`;
    try {
      if (swap) localStorage.setItem(key, JSON.stringify(swap));
      else localStorage.removeItem(key);
    } catch {}
  };

  const [showSwapModal, setShowSwapModal] = useState(false);
  // Raw in-progress text for the cardio number fields (duration / distance /
  // level / calories), keyed `${setIndex}:${field}`. While a field is focused
  // we echo back exactly what was typed instead of the parsed+normalised
  // value — otherwise every keystroke re-derives the number (minutes<->seconds,
  // parseFloat) and the caret jumps to the end, so the first digit of a
  // prefilled value is almost impossible to change. Cleared on blur.
  const [numDrafts, setNumDrafts] = useState({});
  const numDraftKey = (i, f) => `${i}:${f}`;
  const getNumDraft = (i, f) => numDrafts[numDraftKey(i, f)];
  const setNumDraft = (i, f, v) => setNumDrafts(d => ({ ...d, [numDraftKey(i, f)]: v }));
  const clearNumDraft = (i, f) => setNumDrafts(d => {
    if (!(numDraftKey(i, f) in d)) return d;
    const next = { ...d };
    delete next[numDraftKey(i, f)];
    return next;
  });
  const [showBwUpdate, setShowBwUpdate] = useState(false);
  const [newBwValue, setNewBwValue] = useState('');
  const [bwSaving, setBwSaving] = useState(false);
  // Keyed by setIndex so concurrent edits to different sets (or, for a
  // unilateral exercise, the same weight value propagating to both the
  // Right and Left slots at once) each get their own debounce timer —
  // a single shared timer meant a second edit's clearTimeout silently
  // cancelled an unrelated set's still-pending save.
  const saveTimeoutRefs = useRef({}); // { [setIndex]: timeoutId }
  const pendingSavesRef = useRef({}); // { [setIndex]: setData } — flushed on unmount
  const sessionIdRef = useRef(null);   // always reflects latest sessionId for stale-closure-safe offline flush
  const saveSetRef = useRef(null);     // always the latest saveSet, for the same reason
  const onSessionCreatedRef = useRef(onSessionCreated);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [exerciseFlag, setExerciseFlag] = useState(null); // existing flag for current exercise, or null
  const [notifyCoach, setNotifyCoach] = useState(true); // checkbox in skip panel
  const [restSeconds, setRestSeconds] = useState(null); // null=hidden, number=countdown
  const [restTotalSeconds, setRestTotalSeconds] = useState(null); // picked duration, for progress-ring math
  const restIntervalRef = useRef(null);

  const startRestTimer = (seconds) => {
    clearInterval(restIntervalRef.current);
    setRestSeconds(seconds);
    setRestTotalSeconds(seconds);
    restIntervalRef.current = setInterval(() => {
      setRestSeconds(prev => {
        if (prev <= 1) {
          clearInterval(restIntervalRef.current);
          // Haptic on timer end
          Haptics.impact({ style: ImpactStyle.Light }).catch(() => {
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const clearRestTimer = () => {
    clearInterval(restIntervalRef.current);
    setRestSeconds(null);
    setRestTotalSeconds(null);
  };

  // Clear timer on unmount
  useEffect(() => () => clearInterval(restIntervalRef.current), []);

  // Every exercise on the day is its own WorkoutLogger, and each one asks for a sync on reconnect.
  // The work itself lives in offlineSessionService, which runs one sync at a time for the whole
  // page and gives every caller the same result.
  const syncPendingLogs = useCallback(async () => {
    try {
      const pending = await getPendingLogs();
      // session_complete items are handled by ClientProgramView's syncPendingCompletions
      const logsOnly = pending.filter(item => item.type !== 'session_complete' && item.logData);
      // Nothing queued and no offline-started day waiting for its session
      if (logsOnly.length === 0 && !hasPendingOfflineSessions()) return;
      if (logsOnly.length > 0) setIsSyncing(true);
      const { remaining } = await _syncOfflineWorkout();
      setPendingCount(remaining);
    } catch {
      // Left queued — retried on the next reconnect
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Online / offline listeners + SW message handler
  useEffect(() => {
    getPendingLogs().then(logs => {
      setPendingCount(logs.length);
      if ((logs.length > 0 || hasPendingOfflineSessions()) && navigator.onLine) syncPendingLogs();
    }).catch(() => {});

    const handleOnline = () => { setIsOnline(true); syncPendingLogs(); };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const handleSWMessage = (e) => {
      if (e.data?.type === 'SYNC_WORKOUT_LOGS') syncPendingLogs();
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [syncPendingLogs]);

  // Keep sessionIdRef in sync so the unmount cleanup always has the latest value
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { onSessionCreatedRef.current = onSessionCreated; });

  // A day opened offline runs on a temporary session id. When the connection returns and the real
  // session exists, switch to it, and tell the parent: it was never given the temporary id, and
  // it needs the real one to finish the day.
  useEffect(() => onSessionReconciled(({ tempId, realId }) => {
    if (sessionIdRef.current !== tempId) return;
    sessionIdRef.current = realId;
    setSessionId(realId);
    onSessionCreatedRef.current?.(realId);
  }), []);

  // Session swap > coach override > original — used for all type/flag checks
  const resolvedExercise = sessionSwap || exerciseOverride || programExercise.exercise;
  const durationUnit = resolvedExercise?.duration_unit || 'minutes';

  // Detect exercise type — respects the active swap/override
  const getExerciseType = () => {
    const explicitType = resolvedExercise?.exercise_type;
    if (explicitType) return explicitType;
    const category = resolvedExercise?.movement_category || '';
    if (category === 'Cardio') return 'cardio';
    if (category === 'Stretch') return 'stretch';
    return 'resistance';
  };

  const exerciseType = getExerciseType();

  // A unilateral exercise's `sets` array holds one real logged entry per
  // side (prescribedSets * 2 — see initializeWorkout), but the UI groups
  // each Right/Left pair into one row: KG | RIGHT reps | LEFT reps, per
  // pair. Odd set_number (1-based) = Right, even = Left.
  const isUnilateralResistance = exerciseType === 'resistance' && resolvedExercise?.is_unilateral;

  // True when a swap changed the exercise type (e.g. resistance → isometric/stretch).
  // In that case programExercise.reps is a rep count, not a duration — don't use it as one.
  const originalExerciseType = (() => {
    const t = programExercise.exercise?.exercise_type;
    if (t) return t;
    const cat = programExercise.exercise?.movement_category || '';
    if (cat === 'Cardio') return 'cardio';
    if (cat === 'Stretch') return 'stretch';
    return 'resistance';
  })();
  const swapChangedType = !!sessionSwap && exerciseType !== originalExerciseType;

  const loadOverride = async () => {
    // Use batch-preloaded data from ClientProgramView when available (avoids N+1)
    if (preloadedOverride !== undefined) {
      const data = preloadedOverride;
      if (data?.substitute) setExerciseOverride(data.substitute);
      else setExerciseOverride(null);
      setOverrideNote(data?.notes || null);
      return;
    }
    const { data } = await supabase
      .from('client_exercise_overrides')
      .select('substitute_exercise_id, notes, substitute:substitute_exercise_id(id, name, gif_url, exercise_type, movement_category, is_bodyweight, is_assisted, bodyweight_multiplier)')
      .eq('user_id', userId)
      .eq('program_exercise_id', programExercise.id)
      .maybeSingle();
    if (data?.substitute) setExerciseOverride(data.substitute);
    else setExerciseOverride(null);
    setOverrideNote(data?.notes || null);
  };

  const loadExerciseFlag = useCallback(async () => {
    if (!programExercise?.id || !userId) return;
    // Use batch-preloaded data from ClientProgramView when available (avoids N+1)
    if (preloadedFlag !== undefined) {
      setExerciseFlag(preloadedFlag || null);
      return;
    }
    const { data } = await supabase
      .from('exercise_flags')
      .select('*')
      .eq('user_id', userId)
      .eq('program_exercise_id', programExercise.id)
      .maybeSingle();
    setExerciseFlag(data || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programExercise?.id, userId]);

  // Called from skip confirmation — flags the exercise so the coach can review it
  const flagForCoach = async (reason) => {
    if (!programExercise?.id || !userId) return;
    const exName = exerciseOverride?.name || programExercise.exercise?.name || programExercise.name || 'Unknown Exercise';
    const { error } = await supabase
      .from('exercise_flags')
      .upsert({
        user_id: userId,
        program_exercise_id: programExercise.id,
        exercise_id: programExercise.exercise_id || programExercise.id,
        exercise_name: exName,
        notes: reason || null,
        flagged_at: new Date().toISOString(),
        resolved_at: null,
        resolved_by: null,
        resolution_notes: null,
      }, { onConflict: 'user_id,program_exercise_id' });
    if (!error) setExerciseFlag({ notes: reason || null });
  };

  const handleRemoveFlag = async () => {
    if (!programExercise?.id || !userId) return;
    await supabase
      .from('exercise_flags')
      .delete()
      .eq('user_id', userId)
      .eq('program_exercise_id', programExercise.id);
    setExerciseFlag(null);
  };

  useEffect(() => {
    initializeWorkout();
    fetchLastWorkout();
    loadOverride();
    loadExerciseFlag();
    if (userId && programExercise?.id) {
      loadSessionSwap(userId, programExercise.id).then(swap => {
        if (swap !== undefined) { // undefined = query error, keep localStorage value
          setSessionSwap(swap);
          syncSwapToStorage(swap);
        }
      });
    }
    const timeoutRefsAtSetup = saveTimeoutRefs.current;
    return () => {
      // If the user navigates away during the debounce window, flush every
      // still-pending save immediately (there can be more than one now).
      Object.entries(pendingSavesRef.current).forEach(([setIndexStr, setData]) => {
        const setIndex = parseInt(setIndexStr, 10);
        clearTimeout(timeoutRefsAtSetup[setIndex]);
        if (navigator.onLine) {
          // The latest saveSet, not the one from the render this effect was set up in: that one
          // has no session id yet, and saving through it wrote the set with no session at all.
          saveSetRef.current?.(setIndex, setData);
        } else {
          // saveSet closes over stale state; use sessionIdRef for a fresh session_id
          // and build logData directly so queueLog is called synchronously before unmount
          const flushSessionId = liveSessionId(sessionIdRef.current);
          if (flushSessionId == null) return; // no workout to attach the set to
          queueLog({
            session_id: flushSessionId,
            user_id: userId,
            program_exercise_id: programExercise.id,
            exercise_id: activeExercise?.id || programExercise.exercise_id,
            exercise_name: activeExercise?.name || programExercise.exercise?.name || null,
            exercise_type: activeExercise?.exercise_type || programExercise.exercise?.exercise_type || null,
            movement_category: activeExercise?.movement_category || programExercise.exercise?.movement_category || null,
            is_bodyweight: activeExercise?.is_bodyweight ?? programExercise.exercise?.is_bodyweight ?? false,
            is_assisted: activeExercise?.is_assisted ?? programExercise.exercise?.is_assisted ?? false,
            set_number: setData.setNumber,
            weight: setData.weight ? parseFloat(setData.weight) : null,
            reps: setData.reps ? parseInt(setData.reps) : null,
            rir: setData.rir !== null && setData.rir !== undefined ? parseInt(setData.rir) : null,
            rir_feeling: setData.rirFeeling || null,
            completed: setData.completed,
            duration_seconds: setData.duration_seconds ? parseInt(setData.duration_seconds) : null,
            intensity: setData.intensity || null,
            distance_km: setData.distance_km ?? null,
            resistance_level: setData.resistance_level ?? null,
            calories: setData.calories ?? null,
            cardio_notes: setData.cardio_notes || null,
          });
        }
      });
      pendingSavesRef.current = {};
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programExercise.id]);

  // Re-fetch last workout when the active exercise changes (swap applied or removed).
  useEffect(() => {
    if (userId) fetchLastWorkout();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionSwap?.id, exerciseOverride?.id]);

  // `sets` is sized when initializeWorkout first runs (numSets doubled only if
  // is_unilateral is true at that moment). A session/coach swap resolves later and
  // can flip is_unilateral — if it does, the paired Right/Left UI needs sets.length
  // to match the new count, otherwise the last pair's leftIndex points past the
  // array end and handleSetChange throws on the very next keystroke (real crash:
  // user swapped to a unilateral exercise mid-session, Sentry 21 Aug 2026). Skip the
  // very first run since initializeWorkout already ran for the initial value.
  const prevIsUnilateralRef = useRef(isUnilateralResistance);
  useEffect(() => {
    if (prevIsUnilateralRef.current !== isUnilateralResistance) {
      prevIsUnilateralRef.current = isUnilateralResistance;
      initializeWorkout();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnilateralResistance]);

  // Re-fetch swap when app returns from background / another tab.
  // visibilitychange covers web/PWA; 'resume' covers Capacitor Android/iOS.
  useEffect(() => {
    if (!userId || !programExercise?.id) return;
    const refetch = () => loadSessionSwap(userId, programExercise.id).then(swap => {
      if (swap !== undefined) { // undefined = query error, keep localStorage value
        setSessionSwap(swap);
        syncSwapToStorage(swap);
      }
    });
    const handleVisibility = () => { if (document.visibilityState === 'visible') refetch(); };
    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('resume', refetch); // Capacitor fires this on foreground
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('resume', refetch);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, programExercise?.id]);

  const initializeWorkout = async () => {
    // Set once the session is known, so the offline fallback below can tell "no session yet" from
    // "session found, but its saved sets could not be loaded".
    let resolvedSessionId = null;
    let sessionLookup = null;

    // No connection, or none that answers: log into the session this device already knows for
    // the day, or the day's temporary one (shared by every exercise on the day and swapped for
    // the real session the next time syncPendingLogs runs with a connection). The sets start
    // empty, because the saved ones cannot be loaded; they are queued and uploaded later.
    const startWithoutServer = (knownSessionId) => {
      const offlineSessionId = knownSessionId
        || getOfflineSessionId({ userId, programId, dayId, assignedAt });
      sessionIdRef.current = offlineSessionId;
      setSessionId(offlineSessionId);
      const offlinePrescribedSets = programExercise.sets || 3;
      const numSets = isUnilateralResistance ? offlinePrescribedSets * 2 : offlinePrescribedSets;
      const offlineDurationUnit = programExercise.exercise?.duration_unit || 'minutes';
      const prescribedSecs = exerciseType === 'cardio' || exerciseType === 'stretch'
        ? offlineDurationUnit === 'seconds'
          ? (parseInt(programExercise.reps) || 0)
          : (parseInt(programExercise.reps) || 0) * 60
        : exerciseType === 'isometric'
          ? (parseInt(programExercise.reps) || 0)
          : 0;
      setSets(Array.from({ length: numSets }, (_, i) => ({
        setNumber: i + 1,
        weight: '', reps: '', rir: null, rirFeeling: null,
        completed: false, logId: null,
        duration_seconds: prescribedSecs, intensity: null,
        distance_km: null, resistance_level: null, calories: null, cardio_notes: null,
      })));
      setLoading(false);
    };

    // Known to be offline: do not ask the server at all. The requests cannot succeed, and with an
    // expired login each one takes about 25 seconds to say so.
    if (!navigator.onLine) {
      let remembered = null;
      try { remembered = _readFreshSessionLs(userId, programId, dayId); } catch { /* no cache */ }
      if (remembered && onSessionCreated) onSessionCreated(remembered);
      startWithoutServer(remembered);
      return;
    }

    try {
      // All WorkoutLogger instances for the same day share one promise so only
      // a single INSERT ever fires — eliminating the concurrent-insert 409.
      sessionLookup = getOrCreateSession(userId, programId, dayId, assignedAt);
      const { sessionId: currentSessionId, alreadyCompleted } =
        await _awaitSession(sessionLookup, userId, programId, dayId);

      if (alreadyCompleted) {
        setDayAlreadyCompleted(true);
        setLoading(false);
        return;
      }

      resolvedSessionId = currentSessionId;
      sessionIdRef.current = currentSessionId;
      setSessionId(currentSessionId);

      // Notify parent that session was created/found
      if (onSessionCreated && currentSessionId) {
        onSessionCreated(currentSessionId);
      }

      const { data: logs, error: logsError } = await withTimeLimit(supabase
        .from('workout_logs')
        .select('*')
        .eq('session_id', currentSessionId)
        .eq('program_exercise_id', programExercise.id)
        .order('set_number', { ascending: true }));

      if (logsError) throw logsError;

      // A unilateral exercise's prescribed set count is per side (e.g. "3
      // sets" = 3 rows, each capturing a Right rep count and a Left rep
      // count together) — so the real underlying number of logged sets is
      // double the prescribed count, one per side, sharing set_number
      // parity: odd = Right, even = Left. See the paired-row render below.
      const prescribedSets = programExercise.sets || 3;
      const numSets = isUnilateralResistance ? prescribedSets * 2 : prescribedSets;
      const initializedSets = [];

      // For cardio/stretch, reps field stores the value in the exercise's duration_unit
      const exerciseDurationUnit = programExercise.exercise?.duration_unit || 'minutes';
      const prescribedSeconds = exerciseType === 'cardio' || exerciseType === 'stretch'
        ? exerciseDurationUnit === 'seconds'
          ? (parseInt(programExercise.reps) || 0)
          : (parseInt(programExercise.reps) || 0) * 60
        : exerciseType === 'isometric'
          ? (parseInt(programExercise.reps) || 0)
          : 0;

      for (let i = 0; i < numSets; i++) {
        const existingLog = logs?.find(log => log.set_number === i + 1);

        const defaultWeight = existingLog?.weight || '';

        initializedSets.push({
          setNumber: i + 1,
          weight: defaultWeight,
          reps: existingLog?.reps || '',
          rir: existingLog?.rir ?? null,
          rirFeeling: existingLog?.rir_feeling || null,
          completed: existingLog?.completed || false,
          logId: existingLog?.id || null,
          // Cardio fields - store duration in seconds
          duration_seconds: existingLog?.duration_seconds || prescribedSeconds,
          intensity: existingLog?.intensity || null,
          distance_km: existingLog?.distance_km || null,
          resistance_level: existingLog?.resistance_level || null,
          calories: existingLog?.calories || null,
          cardio_notes: existingLog?.cardio_notes || null,
        });
      }

      setSets(initializedSets);
      setLoading(false);
    } catch (error) {
      // Network failure, or no answer in time. The user can still log sets.
      //
      // If the session was found (on the server, or remembered on this device) and only its saved
      // sets could not be loaded, keep that session. Otherwise there is no session yet.
      const isNetErr = !navigator.onLine || error?.timedOut || error?.name === 'TypeError' ||
        error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError');
      if (isNetErr) {
        startWithoutServer(resolvedSessionId);
        // Gave up waiting for the session, but the request is still out there. If it does come
        // back, the day has its real session: pick it up straight away.
        if (error?.timedOut && !resolvedSessionId && sessionLookup) {
          sessionLookup.then((late) => {
            if (late?.alreadyCompleted) setDayAlreadyCompleted(true);
            else syncPendingLogs();
          }, () => {});
        }
        return;
      }
      // Non-network error — show it
      setSets([{ _error: error?.message || 'Failed to initialize workout session' }]);
      setLoading(false);
    }
  };

  const fetchLastWorkout = async () => {
    try {
      // Use the active (possibly swapped) exercise ID so history matches what's on screen
      const activeExerciseId = resolvedExercise?.id || programExercise.exercise_id;
      const activeExerciseName = resolvedExercise?.name || programExercise.exercise?.name;
      const data = await getLastWorkoutForExercise(userId, activeExerciseId, activeExerciseName);
      if (data) {
        setLastWorkout(data);
      }
    } catch (error) {
      // Last workout is non-critical — log for debugging but don't block the UI
      console.error('Failed to fetch last workout:', error);
    }

    // Best estimated 1RM (last 90 days, highest across all sets) — separate
    // from lastWorkout so a lighter recent session doesn't understate it.
    // getBestEstimated1RM returns null for bodyweight/assisted.
    try {
      const activeExerciseName = resolvedExercise?.name || programExercise.exercise?.name;
      const best = await getBestEstimated1RM(
        userId,
        activeExerciseName,
        resolvedExercise?.is_bodyweight,
        resolvedExercise?.is_assisted,
      );
      setBestE1RM(best);
    } catch (error) {
      console.error('Failed to fetch best estimated 1RM:', error);
      setBestE1RM(null);
    }
  };

  const handleSetChange = async (setIndex, field, value) => {
    saveFeedbackRevision.current += 1;
    setSavedSet(null);
    const updatedSets = [...sets];
    updatedSets[setIndex][field] = value;

    // Auto-complete set based on exercise type
    if (!isLocked) {
      const set = updatedSets[setIndex];

      if (exerciseType === 'resistance') {
        if (resolvedExercise?.is_bodyweight) {
          // Bodyweight: complete when reps are entered
          if (field === 'reps' && set.reps) {
            updatedSets[setIndex].completed = true;
          }
        } else {
          // Regular resistance: complete when both weight and reps are entered
          if ((field === 'weight' || field === 'reps') && set.weight && set.reps) {
            updatedSets[setIndex].completed = true;
          }
        }
      } else if (exerciseType === 'cardio' || exerciseType === 'stretch') {
        // Cardio/Stretch: duration is prescribed up front, so entering any
        // metric (or picking an intensity) means the set was performed. Don't
        // require an intensity tap — that left "numbers entered, no chip" sets
        // stuck at completed=false and blocked "Complete Day".
        const performedFields = ['intensity', 'duration_seconds', 'distance_km', 'resistance_level', 'calories'];
        if (performedFields.includes(field) && set[field] !== null && set[field] !== undefined && set[field] !== '') {
          updatedSets[setIndex].completed = true;
        }
      } else if (exerciseType === 'isometric') {
        // Isometric: complete when hold duration is entered
        if (field === 'duration_seconds' && set.duration_seconds) {
          updatedSets[setIndex].completed = true;
        }
      }
    }

    setSets(updatedSets);

    // Save on relevant field changes
    const cardioFields = ['intensity', 'duration_seconds', 'distance_km', 'resistance_level', 'calories', 'cardio_notes'];
    const shouldSave = (exerciseType === 'resistance' && (field === 'weight' || field === 'reps')) ||
                       ((exerciseType === 'cardio' || exerciseType === 'stretch') && cardioFields.includes(field)) ||
                       (exerciseType === 'isometric' && field === 'duration_seconds');

    if (shouldSave) {
      debouncedSave(setIndex, updatedSets[setIndex]);

      // Auto-mark exercise as done when ALL sets are completed
      const allCompleted = updatedSets.every(s => s.completed);
      if (allCompleted && onStatusChange) {
        onStatusChange('done', null);
      }
    }
  };

  // Apply one intensity level to ALL cardio sets at once (interval training UX)
  const handleSharedIntensity = async (value) => {
    if (isLocked) return;
    const updatedSets = sets.map(s => ({ ...s, intensity: value, completed: true }));
    setSets(updatedSets);
    await Promise.all(updatedSets.map((setData, idx) => saveSet(idx, setData)));
    if (onStatusChange) onStatusChange('done', null);
  };

  const debouncedSave = (setIndex, setData) => {
    clearTimeout(saveTimeoutRefs.current[setIndex]);
    pendingSavesRef.current[setIndex] = setData;
    saveTimeoutRefs.current[setIndex] = setTimeout(() => {
      saveSet(setIndex, setData);
      delete pendingSavesRef.current[setIndex];
      delete saveTimeoutRefs.current[setIndex];
    }, 500);
  };

  const saveSet = async (setIndex, setData) => {
    const feedbackRevision = ++saveFeedbackRevision.current;
    setSavedSet(null);
    let logData;
    try {
      // A day opened offline is saved under a temporary id until its session exists; once it
      // does, this is the real one, even if this render still holds the temporary id.
      const activeSessionId = liveSessionId(sessionId);
      if (activeSessionId == null) {
        // Never write a set that belongs to no workout.
        setSaveError('Workout session is not ready yet. Please try again.');
        return;
      }
      logData = {
        session_id: activeSessionId,
        user_id: userId,
        program_exercise_id: programExercise.id,
        exercise_id: (sessionSwap || exerciseOverride)?.id || programExercise.exercise_id,
        // 📸 Snapshot fields - preserve exercise details at time of logging
        exercise_name: (sessionSwap || exerciseOverride)?.name || programExercise.exercise?.name || null,
        exercise_type: (sessionSwap || exerciseOverride)?.exercise_type || exerciseType,
        movement_category: (sessionSwap || exerciseOverride)?.movement_category || programExercise.exercise?.movement_category || null,
        is_bodyweight: (sessionSwap || exerciseOverride)?.is_bodyweight ?? programExercise.exercise?.is_bodyweight ?? false,
        is_assisted: (sessionSwap || exerciseOverride)?.is_assisted ?? programExercise.exercise?.is_assisted ?? false,
        set_number: setData.setNumber,
        weight: setData.weight ? parseFloat(setData.weight) : null,
        reps: setData.reps ? parseInt(setData.reps) : null,
        rir: setData.rir !== null && setData.rir !== undefined ? parseInt(setData.rir) : null,
        rir_feeling: setData.rirFeeling || null,
        completed: setData.completed,
      };

      // Add time-based fields (cardio, stretch, isometric)
      if (exerciseType === 'cardio' || exerciseType === 'stretch' || exerciseType === 'isometric') {
        logData.duration_seconds = setData.duration_seconds ? parseInt(setData.duration_seconds) : null;
        logData.exercise_type = exerciseType;
      }
      if (exerciseType === 'cardio' || exerciseType === 'stretch') {
        logData.intensity = setData.intensity || null;
        logData.distance_km = setData.distance_km ?? null;
        logData.resistance_level = setData.resistance_level ?? null;
        logData.calories = setData.calories ?? null;
        logData.cardio_notes = setData.cardio_notes || null;
      }
      if (exerciseType === 'isometric') {
        // Isometric holds have no weight or reps — clear them
        logData.weight = null;
        logData.reps = null;
      }

      // ── Offline path: queue locally and register background sync ──
      // Also taken while the session exists only on this device: the database cannot take a set
      // under a temporary id, so the set waits in the queue for the session to be created.
      const awaitingSession = isOfflineSessionId(activeSessionId);
      if (!navigator.onLine || awaitingSession) {
        await queueLog(logData);
        setPendingCount(prev => prev + 1);
        _requestBackgroundSync();
        if (onSetCompleted) onSetCompleted(programExercise.id);
        setSaveError(null);
        // Connected, but still on a temporary id: create the session and upload now.
        if (navigator.onLine) syncPendingLogs();
        return;
      }

      if (setData.logId) {
        // Update existing record by ID — fastest path once logId is known
        const { error } = await supabase
          .from('workout_logs')
          .update(logData)
          .eq('id', setData.logId);

        if (error) throw error;
      } else {
        // Atomic upsert — eliminates the SELECT→INSERT race condition that caused
        // "duplicate key value violates unique constraint unique_set_per_session"
        // when two saves for the same set fired concurrently on slow mobile connections.
        const { data: upserted, error } = await supabase
          .from('workout_logs')
          .upsert(logData, { onConflict: 'session_id,program_exercise_id,set_number' })
          .select('id')
          .single();

        if (error) throw error;

        // Cache the returned ID so future saves take the fast update-by-id path
        const updatedSets = [...sets];
        updatedSets[setIndex].logId = upserted.id;
        setSets(updatedSets);
      }

      if (feedbackRevision === saveFeedbackRevision.current) setSavedSet({ number: setData.setNumber });

      // Haptic feedback on successful save (mobile)
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {
        if (navigator.vibrate) navigator.vibrate(50); // web fallback
      });

      // 🏆 Check for PR (only for resistance exercises with weight and reps), once the set is ticked:
      // this save also runs while numbers are typed, which announced records for sets not yet done
      if (
        setData.completed &&
        exerciseType === 'resistance' &&
        setData.weight &&
        setData.reps &&
        programExercise.exercise?.name
      ) {
        const prResult = await checkForPR(
          userId,
          resolvedExercise?.name || programExercise.exercise?.name,
          setData.weight,
          setData.reps,
          resolvedExercise?.is_bodyweight || false,
          resolvedExercise?.is_assisted || false,
          activeSessionId
        );

        if (prResult.isPR) {
          setPRInfo(prev => ({
            ...prev,
            [setIndex]: {
              isPR: true,
              improvement: prResult.improvement,
              previousPR: prResult.previousPR,
              isFirstEver: prResult.isFirstEver,
              repRange: getRepRangeCategory(setData.reps),
            },
          }));

          // Auto-hide PR celebration after 5 seconds
          setTimeout(() => {
            setPRInfo(prev => {
              const updated = { ...prev };
              delete updated[setIndex];
              return updated;
            });
          }, 5000);
        }
      }

      // ⚡ Notify parent that set data was saved (pass peId for local tracking)
      if (onSetCompleted) {
        onSetCompleted(programExercise.id);
      }
      setSaveError(null); // Clear any previous save error
    } catch (error) {
      // If the network dropped mid-save, fall back to the offline queue
      // instead of surfacing an error. navigator.onLine can lag behind the
      // actual connection state, so also check the error type.
      const isNetErr = !navigator.onLine ||
        error?.name === 'TypeError' ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('NetworkError');

      if (isNetErr && logData) {
        try {
          await queueLog(logData);
          setPendingCount(prev => prev + 1);
          _requestBackgroundSync();
          if (onSetCompleted) onSetCompleted(programExercise.id);
          setSaveError(null);
        } catch {
          setSaveError('Failed to save set');
        }
      } else {
        setSaveError(error?.message || 'Failed to save set');
      }
    }
  };
  saveSetRef.current = saveSet;

  // eslint-disable-next-line no-unused-vars
  const toggleSetComplete = async (setIndex) => {
    if (isLocked) return;
    const updatedSets = [...sets];
    updatedSets[setIndex].completed = !updatedSets[setIndex].completed;
    setSets(updatedSets);
    await saveSet(setIndex, updatedSets[setIndex]);

    // ⚡ Notify parent immediately that a set was completed (pass peId for local tracking)
    if (onSetCompleted) {
      onSetCompleted(programExercise.id);
    }

    // Auto-mark exercise as done when all sets are completed
    const allCompleted = updatedSets.every(s => s.completed);
    if (allCompleted && onStatusChange) {
      onStatusChange('done', null);
    }
  };

  const handleBwUpdate = async () => {
    const val = parseFloat(newBwValue);
    if (!val || val <= 0) return;
    setBwSaving(true);
    // A weigh-in in the one weight record (migration 080); the database sets the current weight
    try {
      await recordWeighIn(userId, val, { notes: 'Updated during a workout' });
    } catch {
      setSaveError('Could not save bodyweight. Please try again.');
      setBwSaving(false);
      return;
    }
    setSaveError(null);
    setShowBwUpdate(false);
    setNewBwValue('');
    setBwSaving(false);
  };

  // Handle marking exercise as done manually
  const handleMarkDone = async () => {
    if (isLocked) return;
    if (exerciseStatus?.status === 'done') {
      onStatusChange?.(null, null);
    } else {
      onStatusChange?.('done', null);
      setShowSkipReasons(false);
    }
  };

  // Handle skipping the exercise
  const handleSkip = () => {
    if (isLocked) return;
    if (exerciseStatus?.status === 'skipped') {
      onStatusChange?.(null, null);
      setShowSkipReasons(false);
      setSkipReasonText('');
    } else {
      setShowSkipReasons(true);
    }
  };

  // Handle confirming skip with reason
  const handleConfirmSkip = async () => {
    if (isLocked) return;
    if (!skipReasonText.trim()) return;
    onStatusChange?.('skipped', skipReasonText.trim());
    if (notifyCoach) await flagForCoach(skipReasonText.trim());
    setShowSkipReasons(false);
  };

  // Handle quick select skip reason
  const handleQuickSkipReason = (reason) => {
    if (isLocked) return;
    setSkipReasonText(reason);
  };

  // Initialize skipReasonText when component loads with existing reason
  useEffect(() => {
    if (exerciseStatus?.skippedReason) {
      setSkipReasonText(exerciseStatus.skippedReason);
    }
  }, [exerciseStatus?.skippedReason]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  if (dayAlreadyCompleted) {
    const exerciseNameForCard = programExercise.exercise?.name || programExercise.name || 'Exercise';
    return (
      <div style={{
        ...styles.container,
        background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.06) 0%, rgba(52, 199, 89, 0.02) 100%)',
        border: '1px solid rgba(52, 199, 89, 0.25)',
        borderRadius: '14px',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(52, 199, 89, 0.15)',
          border: '1.5px solid rgba(52, 199, 89, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '18px',
        }}>
          ✓
        </div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: theme.success, marginBottom: '2px' }}>
            Workout already completed
          </div>
          <div style={{ fontSize: '13px', color: theme.textMuted }}>
            {exerciseNameForCard} — this day has been logged for the current training block.
          </div>
        </div>
      </div>
    );
  }

  const activeExercise = sessionSwap || exerciseOverride || null;
  const exerciseName = activeExercise?.name || programExercise.exercise?.name || programExercise.name || 'Unknown Exercise';
  const gifUrl = activeExercise?.gif_url || programExercise.exercise?.gif_url || programExercise.gif_url;
  const videoUrl = programExercise.exercise?.video_url || programExercise.video_url;

  // Exercise type accent colors
  const accentColor = exerciseType === 'cardio' ? '#007AFF' : exerciseType === 'isometric' ? '#AF52DE' : brandColors.gold;
  const accentBg = exerciseType === 'cardio' ? 'rgba(0, 122, 255, 0.1)' : exerciseType === 'isometric' ? 'rgba(175, 82, 222, 0.1)' : 'rgba(212, 175, 55, 0.1)';

  // Secondary actions: the YouTube tutorial (only where there is one) and the swap.
  // Outlined, under the gold button; Skip stays last as a text link.
  const secondaryActions = (videoUrl || !isLocked) && (
    <div style={styles.secondaryRow}>
      {videoUrl && (
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.secondaryBtn}
        >
          <span aria-hidden="true">▶</span> Tutorial
        </a>
      )}
      {!isLocked && (
        <button
          type="button"
          onClick={() => setShowSwapModal(true)}
          style={{
            ...styles.secondaryBtn,
            ...(sessionSwap ? { color: theme.success, borderColor: theme.success } : {}),
          }}
        >
          {sessionSwap ? '⇄ Swapped' : '⇄ Swap'}
        </button>
      )}
    </div>
  );

  return (
    <div style={{
      ...styles.container,
      borderLeft: `3px solid ${accentColor}`,
    }}>
      {/* Offline / syncing banner */}
      {!isOnline && (
        <div style={{
          backgroundColor: 'rgba(255, 149, 0, 0.15)',
          border: '1px solid rgba(255, 149, 0, 0.4)',
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 12,
          fontSize: 12,
          fontWeight: 600,
          color: theme.warning,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span>●</span>
          Offline — sets saving locally{pendingCount > 0 ? ` (${pendingCount} queued)` : ''}
        </div>
      )}
      {isOnline && isSyncing && (
        <div style={{
          backgroundColor: 'rgba(212, 175, 55, 0.1)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 12,
          fontSize: 12,
          fontWeight: 600,
          color: theme.gold,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span>↑</span>
          Syncing {pendingCount} saved set{pendingCount !== 1 ? 's' : ''}...
        </div>
      )}

      {/* Expanded GIF Modal */}
      {gifExpanded && gifUrl && (
        <div style={styles.modalOverlay} onClick={() => setGifExpanded(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <ExerciseDemo
              gifUrl={gifUrl}
              alt={exerciseName}
              style={styles.expandedGif}
              onError={() => setGifExpanded(false)}
            />
            <button style={styles.closeBtn} onClick={() => setGifExpanded(false)}>
              ✕
            </button>
          </div>
        </div>
      )}

      <div style={{ ...styles.mainRow, ...(exerciseType === 'cardio' ? { flexDirection: 'column' } : {}) }} className="workout-logger-row">
        {/* Right: Exercise Info & Logging */}
        <div style={styles.contentContainer}>
          {/* Header */}
          <div style={styles.header}>
            {/* The demo, small, beside the name. Tap to see it large. If it cannot load, a quiet
                placeholder keeps its place, so the card does not jump (15 October 2026). */}
            {gifUrl && failedDemoUrl === gifUrl && (
              <div style={{ ...styles.gifContainer, ...styles.demoUnavailable }} role="img" aria-label={'Demo for ' + exerciseName + ' unavailable'}>
                <span aria-hidden="true" style={{ fontSize: '22px', opacity: 0.6 }}>▶</span>
                <span aria-hidden="true">Demo unavailable</span>
              </div>
            )}
            {gifUrl && failedDemoUrl !== gifUrl && (
              <button
                type="button"
                key={gifUrl}
                style={styles.gifContainer}
                className="workout-logger-thumb"
                onClick={() => setGifExpanded(true)}
                aria-label={'Show the ' + exerciseName + ' demo larger'}
              >
                <ExerciseDemo
                  gifUrl={gifUrl}
                  alt=""
                  style={styles.gif}
                  onError={() => setFailedDemoUrl(gifUrl)}
                />
                <span style={styles.expandHint} aria-hidden="true">⤢</span>
              </button>
            )}
            <div style={{flex: 1, minWidth: 0}}>
              <span style={styles.exerciseName}>{exerciseName}</span>
              {exerciseOverride && !sessionSwap && (
                <span style={{ fontSize: 11, backgroundColor: 'rgba(212,175,55,0.2)', color: theme.gold,
                                borderRadius: 4, padding: '2px 6px', marginLeft: 6, fontWeight: 600 }}>
                  MODIFIED
                </span>
              )}
              {sessionSwap && (
                <span style={{ fontSize: 11, backgroundColor: 'rgba(52,199,89,0.2)', color: theme.success,
                                borderRadius: 4, padding: '2px 6px', marginLeft: 6, fontWeight: 600 }}>
                  SWAPPED
                </span>
              )}
              {overrideNote && (
                <div style={{ marginTop: 6, padding: '6px 10px', background: 'rgba(212,175,55,0.08)', borderLeft: `2px solid ${theme.gold}`, borderRadius: '0 6px 6px 0', fontSize: 12, color: theme.gold, fontStyle: 'italic' }}>
                  Coach: {overrideNote}
                </div>
              )}
              {!overrideNote && coachResolvedNote && (
                <div style={{ marginTop: 6, padding: '6px 10px', background: 'rgba(52,199,89,0.08)', borderLeft: `2px solid ${theme.success}`, borderRadius: '0 6px 6px 0', fontSize: 12, color: theme.success, fontStyle: 'italic' }}>
                  Coach: {coachResolvedNote}
                </div>
              )}
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center', marginTop: '4px'}}>
                <span style={{
                  ...styles.target,
                  backgroundColor: accentBg,
                  borderLeft: `2px solid ${accentColor}`,
                  // A long coach's note beside it used to squeeze this into "3 / x / 10".
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {exerciseType === 'cardio' || exerciseType === 'stretch'
                    ? swapChangedType ? `${programExercise.sets} sets` : `${programExercise.sets} x ${programExercise.reps} min`
                    : exerciseType === 'isometric'
                      ? swapChangedType ? `${programExercise.sets} sets` : `${programExercise.sets} x ${parseInt(programExercise.reps) || 0}s hold`
                      : `${programExercise.sets} x ${programExercise.reps}`
                  }
                </span>
                {exerciseType === 'resistance' && (
                  resolvedExercise?.is_assisted
                    ? (
                      <span style={{
                        fontSize: '13px',
                        color: brandColors.gold,
                        fontWeight: '600',
                        padding: '4px 10px',
                        backgroundColor: 'rgba(212, 175, 55, 0.1)',
                        borderRadius: '8px',
                      }}>
                        Assisted
                      </span>
                    )
                    : resolvedExercise?.is_bodyweight ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          fontSize: '13px', color: brandColors.gold, fontWeight: '600',
                          padding: '4px 10px', backgroundColor: 'rgba(212, 175, 55, 0.1)', borderRadius: '8px',
                        }}>
                          BW
                        </span>
                        {!isLocked && !showBwUpdate && (
                          <button
                            onClick={() => setShowBwUpdate(true)}
                            style={{
                              fontSize: '11px', color: brandColors.gold, background: 'none',
                              border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0,
                            }}
                          >
                            Update
                          </button>
                        )}
                        {showBwUpdate && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                              type="number" inputMode="decimal" placeholder="kg" value={newBwValue}
                              onChange={e => setNewBwValue(e.target.value)}
                              style={{
                                width: '60px', padding: '4px 8px', borderRadius: '8px',
                                border: '1px solid rgba(212,175,55,0.4)', background: theme.surface2,
                                color: theme.text, fontSize: '13px', outline: 'none',
                              }}
                              autoFocus
                            />
                            <button
                              onClick={handleBwUpdate} disabled={bwSaving}
                              style={{
                                padding: '4px 10px', borderRadius: '8px', border: 'none',
                                background: brandColors.gold, color: theme.bg,
                                fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                              }}
                            >
                              {bwSaving ? '...' : 'Save'}
                            </button>
                            <button
                              onClick={() => { setShowBwUpdate(false); setNewBwValue(''); setSaveError(null); }}
                              style={{
                                padding: '4px 8px', borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.1)', background: 'none',
                                color: theme.textMuted, fontSize: '12px', cursor: 'pointer',
                              }}
                            >
                              ✕
                            </button>
                            {saveError && (
                              <span style={{ fontSize: '11px', color: theme.danger }}>
                                {saveError}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    ) : programExercise.prescribed_weight !== null && programExercise.prescribed_weight !== undefined && (
                      <span style={{
                        fontSize: '13px',
                        color: brandColors.gold,
                        fontWeight: '600',
                        padding: '4px 10px',
                        backgroundColor: 'rgba(212, 175, 55, 0.1)',
                        borderRadius: '8px',
                      }}>
                        {programExercise.prescribed_weight}kg
                      </span>
                    )
                )}
                {programExercise.notes && (
                  <span style={{
                    fontSize: '11px',
                    color: brandColors.silver,
                    fontStyle: 'italic',
                    flex: '1 1 140px',
                    minWidth: 0,
                  }}>
                    {programExercise.notes}
                  </span>
                )}
                {/* Estimated 1RM — resistance only, not bodyweight/assisted.
                    Prefer the best e1RM over the last 90 days; fall back to the
                    last session's top set only when there's no 90-day best. */}
                {exerciseType === 'resistance' &&
                  !resolvedExercise?.is_bodyweight &&
                  !resolvedExercise?.is_assisted &&
                  (() => {
                    let orm = null;
                    let source = null;
                    if (bestE1RM?.estimated1RM) {
                      orm = bestE1RM.estimated1RM;
                      source = `Best of last 90 days — ${bestE1RM.weight}kg × ${bestE1RM.reps}` +
                        (bestE1RM.date ? ` on ${new Date(bestE1RM.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : '');
                    } else if (lastWorkout?.weight && lastWorkout?.reps) {
                      orm = calculateOneRM(lastWorkout.weight, lastWorkout.reps);
                      source = `From last session — ${lastWorkout.weight}kg × ${lastWorkout.reps}`;
                    }
                    if (!orm) return null;
                    return (
                      <span title={source} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: '11px', fontWeight: 700,
                        color: brandColors.gold,
                        backgroundColor: 'rgba(212,175,55,0.1)',
                        border: '1px solid rgba(212,175,55,0.25)',
                        borderRadius: '8px',
                        padding: '2px 8px',
                        marginTop: 2,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>
                        Est. 1RM ~{Math.round(orm)}kg
                      </span>
                    );
                  })()}
              </div>
            </div>
            {/* Flag indicator — shown when coach has been notified */}
            {exerciseFlag && (
              <span title="Coach notified" style={{ fontSize: 16, flexShrink: 0 }}>🚩</span>
            )}
          </div>

          {/* Init error */}
          {sets[0]?._error && (
            <div style={{ background: 'rgba(255,69,58,0.15)', border: '1px solid rgba(255,69,58,0.4)', borderRadius: '8px', padding: '12px 16px', margin: '8px 0', color: theme.danger, fontSize: '13px' }}>
              Session error: {sets[0]._error}
            </div>
          )}

          {savedSet && !saveError && <p role="status" style={{ color: theme.success, fontSize: '13px', margin: '8px 0' }}>Set {savedSet.number} · Saved</p>}
          {/* Save error */}
          {saveError && (
            <div style={{ background: 'rgba(255,69,58,0.15)', border: '1px solid rgba(255,69,58,0.4)', borderRadius: '8px', padding: '12px 16px', margin: '8px 0', color: theme.danger, fontSize: '13px' }}>
              Save error: {saveError}
            </div>
          )}

          {/* Set progress indicator */}
          {sets.filter(s => !s._error).length > 0 && (() => {
            const validSets = sets.filter(s => !s._error);
            // For a unilateral exercise, `sets` holds two real logged entries
            // per displayed row (Right + Left) — count by pair so this badge
            // matches what's actually shown (e.g. "2 / 3 sets", not "4 / 6").
            let completedCount, totalCount;
            if (isUnilateralResistance) {
              totalCount = Math.ceil(validSets.length / 2);
              completedCount = 0;
              for (let i = 0; i < validSets.length; i += 2) {
                if (validSets[i]?.completed && validSets[i + 1]?.completed) completedCount++;
              }
            } else {
              completedCount = validSets.filter(s => s.completed).length;
              totalCount = validSets.length;
            }
            const allDone = completedCount === totalCount;
            return (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: allDone ? 'rgba(52, 199, 89, 0.15)' : 'rgba(142, 142, 147, 0.12)',
                border: `1px solid ${allDone ? 'rgba(52, 199, 89, 0.4)' : 'rgba(142, 142, 147, 0.25)'}`,
                color: allDone ? theme.success : brandColors.silver,
              }}>
                {completedCount} / {totalCount} sets
              </div>
            );
          })()}

          {/* Sets */}
          <div style={styles.setsGrid}>
            {isUnilateralResistance ? (() => {
              // Paired Right/Left layout: KG | RIGHT reps | LEFT reps, one
              // row per prescribed set. `sets` holds two real logged entries
              // per row (see initializeWorkout) — pair.rightIndex/leftIndex
              // are indices into that flat array, reused with the exact
              // same handleSetChange/saveSet path as every other exercise
              // type so completion, PR detection, and offline sync all work
              // unchanged.
              const validSets = sets.filter(s => !s._error);
              const pairs = [];
              // Only form a pair when both sides actually exist in `sets` — an
              // orphaned trailing entry (e.g. sets.length briefly odd right after
              // a swap flips is_unilateral, before the resize effect catches up)
              // would otherwise get a leftIndex past the array end, and typing
              // into that box throws in handleSetChange.
              for (let i = 0; i + 1 < validSets.length; i += 2) {
                pairs.push({
                  right: validSets[i], left: validSets[i + 1],
                  rightIndex: i, leftIndex: i + 1,
                  pairNumber: Math.floor(i / 2) + 1,
                });
              }
              const nextPairIndex = pairs.findIndex(p => !(p.right?.completed && p.left?.completed));

              const PrBadge = ({ info }) => info?.isPR ? (
                <div style={{
                  marginTop: '8px', padding: '10px 14px',
                  backgroundColor: 'rgba(212, 175, 55, 0.15)',
                  border: `2px solid ${brandColors.gold}`, borderRadius: '8px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <span style={{ fontSize: '20px' }}>🎉</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: brandColors.gold }}>
                      {info.isFirstEver ? 'FIRST PR!' : 'NEW PR!'}
                    </div>
                    <div style={{ fontSize: '11px', color: brandColors.silver }}>
                      {info.isFirstEver ? `${info.repRange} rep range` : `+${info.improvement.toFixed(1)} kg improvement`}
                    </div>
                  </div>
                  <span style={{ fontSize: '20px' }}>🏆</span>
                </div>
              ) : null;

              return (
                <>
                  {/* Column headers */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px' }}>
                    <span style={{ ...styles.setLabel, visibility: 'hidden' }}>Set</span>
                    {['', 'RIGHT', 'LEFT'].map((label, i) => (
                      <span key={i} style={{ flex: 1, textAlign: 'center', color: brandColors.gold, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {label}
                      </span>
                    ))}
                  </div>
                  {pairs.map((pair, pairIdx) => {
                    const bothCompleted = !!(pair.right?.completed && pair.left?.completed);
                    const isNextPair = !isLocked && pairIdx === nextPairIndex;
                    const hasAnyValue = pair.right?.weight || pair.right?.reps || pair.left?.reps;
                    const weightValue = pair.right?.weight ?? pair.left?.weight ?? '';
                    return (
                      <React.Fragment key={pairIdx}>
                        <div
                          style={{
                            ...styles.setRow,
                            ...(bothCompleted
                              ? { border: `1px solid ${theme.success}`, backgroundColor: 'rgba(52, 199, 89, 0.08)', borderRadius: '8px', padding: '6px 8px' }
                              : isNextPair
                                ? { border: '1px solid rgba(212,175,55,0.5)', backgroundColor: 'rgba(212,175,55,0.06)', borderRadius: '8px', padding: '6px 8px' }
                                : hasAnyValue
                                  ? { border: '1px solid #555', backgroundColor: '#222', borderRadius: '8px', padding: '6px 8px' }
                                  : { border: `1px solid ${theme.surface3}`, backgroundColor: theme.surface1, borderRadius: '8px', padding: '6px 8px' }
                            ),
                          }}
                          className="set-row"
                        >
                          <span style={styles.setLabel} className="set-label">Set {pair.pairNumber}</span>

                          {resolvedExercise?.is_bodyweight ? (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(212, 175, 55, 0.1)', borderRadius: '8px', padding: '10px', fontSize: '14px', fontWeight: '700', color: brandColors.gold }}>
                              BW
                            </div>
                          ) : (
                            <div style={{ position: 'relative', flex: 1 }}>
                              <input
                                type="number" inputMode="decimal" placeholder="kg"
                                value={weightValue}
                                onChange={(e) => {
                                  handleSetChange(pair.rightIndex, 'weight', e.target.value);
                                  handleSetChange(pair.leftIndex, 'weight', e.target.value);
                                }}
                                style={{ ...styles.input, width: '100%' }}
                                className="set-input"
                                disabled={isLocked}
                              />
                            </div>
                          )}

                          <div style={{ position: 'relative', flex: 1 }}>
                            <input
                              type="number" inputMode="numeric" placeholder="Reps"
                              value={pair.right?.reps ?? ''}
                              onChange={(e) => handleSetChange(pair.rightIndex, 'reps', e.target.value)}
                              style={{ ...styles.input, width: '100%' }}
                              className="set-input"
                              disabled={isLocked}
                            />
                          </div>

                          <div style={{ position: 'relative', flex: 1 }}>
                            <input
                              type="number" inputMode="numeric" placeholder="Reps"
                              value={pair.left?.reps ?? ''}
                              onChange={(e) => handleSetChange(pair.leftIndex, 'reps', e.target.value)}
                              style={{ ...styles.input, width: '100%' }}
                              className="set-input"
                              disabled={isLocked}
                            />
                          </div>
                        </div>

                        <PrBadge info={prInfo[pair.rightIndex]} />
                        <PrBadge info={prInfo[pair.leftIndex]} />
                      </React.Fragment>
                    );
                  })}
                </>
              );
            })() : (() => {
              const validSets = sets.filter(s => !s._error);
              const nextSetIndex = validSets.findIndex(s => !s.completed);
              return validSets.map((set, index) => {
              const isNextSet = !isLocked && index === nextSetIndex;
              return (
              <React.Fragment key={index}>
                {(exerciseType === 'cardio' || exerciseType === 'stretch') ? (
                  // ── Cardio / Stretch: two-line card ────────────
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    width: '100%',
                    boxSizing: 'border-box',
                    ...(set.completed
                      ? { border: `1px solid ${theme.success}`, backgroundColor: 'rgba(52, 199, 89, 0.08)' }
                      : { border: `1px solid ${theme.surface3}`, backgroundColor: theme.surface1 }
                    ),
                  }}>
                    {/* Line 1: Set label + duration */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#007AFF', fontSize: '12px', fontWeight: '600', minWidth: '34px' }}>
                        Set {set.setNumber}
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max={durationUnit === 'seconds' ? 3600 : 300}
                        value={
                          getNumDraft(index, 'duration_seconds') ?? (
                            set.duration_seconds
                              ? durationUnit === 'seconds'
                                ? set.duration_seconds
                                : Math.round(set.duration_seconds / 60)
                              : swapChangedType ? '' : (parseInt(programExercise.reps) || '')
                          )
                        }
                        onChange={e => {
                          const raw = e.target.value;
                          setNumDraft(index, 'duration_seconds', raw);
                          const n = parseInt(raw, 10);
                          handleSetChange(
                            index, 'duration_seconds',
                            raw && !Number.isNaN(n)
                              ? durationUnit === 'seconds' ? n : n * 60
                              : null
                          );
                        }}
                        onBlur={() => clearNumDraft(index, 'duration_seconds')}
                        disabled={isLocked}
                        style={{
                          width: '52px',
                          backgroundColor: theme.bg,
                          border: `1px solid ${theme.surface3}`,
                          borderRadius: '8px',
                          padding: '6px 4px',
                          color: theme.text,
                          fontSize: '15px',
                          fontWeight: '700',
                          textAlign: 'center',
                          outline: 'none',
                        }}
                      />
                      <span style={{ color: theme.textMuted, fontSize: '12px' }}>
                        {durationUnit === 'seconds' ? 'sec' : 'min'}
                      </span>
                    </div>

                    {/* Line 2: Universal metrics (cardio only) */}
                    {exerciseType === 'cardio' && (
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        width: '100%',
                        overflow: 'hidden',
                        backgroundColor: 'rgba(0, 122, 255, 0.06)',
                        borderRadius: '8px',
                        padding: '8px 6px',
                        boxSizing: 'border-box',
                      }}>
                        {[
                          { field: 'distance_km',     label: 'DISTANCE', unit: 'km',  step: '0.1', inputMode: 'decimal' },
                          { field: 'resistance_level', label: 'LEVEL',    unit: 'lvl', step: '1',   inputMode: 'numeric' },
                          { field: 'calories',         label: 'CALORIES', unit: 'cal', step: '1',   inputMode: 'numeric' },
                        ].map(({ field, label, unit, step, inputMode }) => (
                          <div key={field} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }}>
                            <span style={{ color: theme.textMuted, fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>{label}</span>
                            <input
                              type="number"
                              inputMode={inputMode}
                              min="0"
                              step={step}
                              value={getNumDraft(index, field) ?? (set[field] ?? '')}
                              onChange={e => {
                                const raw = e.target.value;
                                setNumDraft(index, field, raw);
                                const n = parseFloat(raw);
                                handleSetChange(index, field, raw !== '' && !Number.isNaN(n) ? n : null);
                              }}
                              onBlur={() => clearNumDraft(index, field)}
                              placeholder="0"
                              disabled={isLocked}
                              style={{
                                width: '100%',
                                minWidth: 0,
                                backgroundColor: theme.bg,
                                border: '1px solid #3A3A4A',
                                borderRadius: '8px',
                                padding: '6px 2px',
                                color: theme.text,
                                fontSize: '14px',
                                fontWeight: '700',
                                outline: 'none',
                                textAlign: 'center',
                                boxSizing: 'border-box',
                              }}
                            />
                            <span style={{ color: '#555', fontSize: '11px', fontWeight: '500' }}>{unit}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                <div style={{
                  ...styles.setRow,
                  ...(set.completed
                    ? { border: `1px solid ${theme.success}`, backgroundColor: 'rgba(52, 199, 89, 0.08)', borderRadius: '8px', padding: '6px 8px' }
                    : isNextSet
                      ? { border: '1px solid rgba(212,175,55,0.5)', backgroundColor: 'rgba(212,175,55,0.06)', borderRadius: '8px', padding: '6px 8px' }
                      : (exerciseType === 'resistance'
                          ? (set.weight || set.reps)
                          : exerciseType === 'isometric'
                            ? set.duration_seconds
                            : set.intensity)
                        ? { border: '1px solid #555', backgroundColor: '#222', borderRadius: '8px', padding: '6px 8px' }
                        : { border: `1px solid ${theme.surface3}`, backgroundColor: theme.surface1, borderRadius: '8px', padding: '6px 8px' }
                  ),
                }} className="set-row">
                  <span style={{
                    ...styles.setLabel,
                    color: exerciseType === 'isometric' ? '#AF52DE' : brandColors.silver
                  }} className="set-label">
                    {resolvedExercise?.is_unilateral
                      // No left/right field on a set — alternate the label so it's
                      // clear which side each logged set represents, since the
                      // weight/reps you enter should be the real per-side numbers.
                      ? (set.setNumber % 2 === 1 ? 'Left' : 'Right')
                      : `Set ${set.setNumber}`}
                  </span>

                  {exerciseType === 'isometric' ? (
                    // Isometric inputs (hold duration in seconds)
                    <IsometricInput
                      set={set}
                      setIndex={index}
                      onChange={handleSetChange}
                      isLocked={isLocked}
                    />
                  ) : (
                    // Resistance training inputs (weight x reps)
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
                      {resolvedExercise?.is_bodyweight ? (
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(212, 175, 55, 0.1)',
                          borderRadius: '8px',
                          padding: '10px',
                          fontSize: '14px',
                          fontWeight: '700',
                          color: brandColors.gold,
                        }}>
                          BW
                        </div>
                      ) : (
                        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                          {/* Fills its half of the row, so a weight like 23.75 is not cut off. */}
                          <input
                            type="number"
                            inputMode="decimal"
                            placeholder="kg"
                            value={set.weight}
                            onChange={(e) => handleSetChange(index, 'weight', e.target.value)}
                            style={styles.inputWide}
                            className="set-input"
                            disabled={isLocked}
                          />
                        </div>
                      )}
                      <span style={styles.x}>x</span>
                      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                        <input
                          type="number"
                          inputMode="numeric"
                          placeholder="Reps"
                          value={set.reps}
                          onChange={(e) => handleSetChange(index, 'reps', e.target.value)}
                          style={styles.inputWide}
                          className="set-input"
                          disabled={isLocked}
                        />
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* PR Celebration Banner */}
                {prInfo[index]?.isPR && (
                  <div style={{
                    marginTop: '8px',
                    marginBottom: '8px',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(212, 175, 55, 0.15)',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: brandColors.gold,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}>
                    <span style={{ fontSize: '24px' }}>🎉</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: brandColors.gold,
                        marginBottom: '2px',
                      }}>
                        {prInfo[index].isFirstEver ? 'FIRST PR!' : 'NEW PR!'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: brandColors.silver,
                      }}>
                        {prInfo[index].isFirstEver ? (
                          `${prInfo[index].repRange} rep range`
                        ) : (
                          `+${prInfo[index].improvement.toFixed(1)} kg improvement`
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '24px' }}>🏆</span>
                  </div>
                )}
              </React.Fragment>
              );
            });
          })()}
          </div>

          {/* Shared intensity selector — cardio only, shown once below all sets */}
          {exerciseType === 'cardio' && (
            <div style={{ marginTop: '14px' }}>
              <div style={{ color: theme.textMuted, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                Intensity
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                {CARDIO_INTENSITY_LEVELS.map((level) => {
                  const sharedIntensity = sets.length > 0 && sets.every(s => s.intensity === sets[0].intensity) ? sets[0].intensity : null;
                  const isSelected = sharedIntensity === level.value;
                  return (
                    <button
                      key={level.value}
                      onClick={() => handleSharedIntensity(level.value)}
                      disabled={isLocked}
                      title={`${level.label} — ${level.description} (${level.hrZone})`}
                      style={{
                        padding: '10px 4px',
                        border: `1px solid ${isSelected ? level.color : theme.surface3}`,
                        borderRadius: '8px',
                        backgroundColor: isSelected ? level.color : theme.bg,
                        color: isSelected ? theme.text : theme.textMuted,
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        opacity: isLocked ? 0.5 : 1,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {level.shortLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rest Timer — resistance only, shown after first set completed */}
          {exerciseType === 'resistance' && !isLocked && sets.some(s => s.completed) && !sets.every(s => s.completed) && (
            <div style={{
              marginTop: '10px',
              padding: '14px',
              backgroundColor: brandColors.darkGray,
              borderRadius: '14px',
              border: restSeconds !== null
                ? `1px solid ${restSeconds === 0 ? theme.success : 'rgba(212,175,55,0.4)'}`
                : '1px solid rgba(255,255,255,0.06)',
            }}>
              <RestTimer
                restSeconds={restSeconds}
                totalSeconds={restTotalSeconds}
                onSelect={startRestTimer}
                onClear={clearRestTimer}
              />
            </div>
          )}

          {/* Weight hint — only for non-bodyweight resistance exercises. The
              "two dumbbells summed" framing only applies bilaterally — a
              unilateral exercise uses one weight per side, entered once and
              shared across both the Right and Left columns. */}
          {/* 15 October 2026: one short line, with the example one tap away. The full sentence under
              every exercise was the same text six times on one screen. */}
          {exerciseType === 'resistance' && !resolvedExercise?.is_bodyweight && !resolvedExercise?.is_assisted && (
            <div style={{
              fontSize: 12,
              color: theme.textMuted,
              textAlign: 'center',
              marginBottom: 6,
              lineHeight: 1.4,
            }}>
              <button
                type="button"
                onClick={() => setWeightHelpOpen(open => !open)}
                aria-expanded={weightHelpOpen}
                style={{
                  background: 'none', border: 'none', padding: '4px 6px', cursor: 'pointer',
                  color: theme.textMuted, fontSize: 12, fontFamily: 'inherit',
                }}
              >
                kg = <span style={{ color: theme.gold, fontWeight: 600 }}>{isUnilateralResistance ? 'weight for one side' : "total weight you're holding"}</span>
                <span aria-hidden="true" style={{ marginLeft: 6 }}>ⓘ</span>
              </button>
              {weightHelpOpen && (
                <div>
                  {isUnilateralResistance
                    ? 'e.g. a 15kg dumbbell = 15kg, the same for both Right and Left'
                    : 'e.g. two 15kg dumbbells = 30kg'}
                </div>
              )}
            </div>
          )}

          {/* Hint for assisted exercises (e.g. Assisted Dip, Assisted Pull-Up) */}
          {exerciseType === 'resistance' && resolvedExercise?.is_assisted && (
            <div style={{
              fontSize: 11,
              color: theme.textMuted,
              textAlign: 'center',
              marginBottom: 6,
              lineHeight: 1.4,
            }}>
              Enter the <span style={{ color: theme.gold, fontWeight: 600 }}>assistance (counterweight)</span> — the more kg, the easier. e.g. 40kg assistance = machine helping you lift 40kg of your bodyweight
            </div>
          )}

          {/* Last Workout - cross-program history (resistance + isometric) */}
          {(exerciseType === 'resistance' || exerciseType === 'isometric') && lastWorkout && (lastWorkout.weight != null || lastWorkout.reps != null || lastWorkout.duration_seconds != null) && (
            <div style={styles.lastWorkoutContainer}>
              <div style={styles.lastWorkout}>
                <span style={styles.lastWorkoutLabel}>Last Workout:</span>
                <span style={styles.lastWorkoutValue}>
                  {exerciseType === 'isometric'
                    ? `${lastWorkout.duration_seconds ?? '?'}s hold`
                    : resolvedExercise?.is_assisted
                      ? `Assisted -${lastWorkout.weight ?? '?'}kg`
                      : resolvedExercise?.is_bodyweight
                        ? (lastWorkout.weight ? `BW +${lastWorkout.weight}kg` : 'BW')
                        : `${lastWorkout.weight ?? '?'}kg × ${lastWorkout.reps ?? '?'} reps`
                  }
                </span>
                {lastWorkout.program && (
                  <span style={styles.lastWorkoutProgram}>
                    from {lastWorkout.program}
                  </span>
                )}
                {!resolvedExercise?.is_assisted && !resolvedExercise?.is_bodyweight &&
                  lastWorkout.weight != null && (sets[0]?.weight || 0) > lastWorkout.weight && (
                  <span style={styles.improvement}>
                    +{((sets[0].weight || 0) - lastWorkout.weight).toFixed(1)} kg
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowHistoryModal(true)}
                style={styles.historyBtn}
                title="View complete exercise history"
              >
                History
              </button>
            </div>
          )}

          {/* Done / Skipped Toggle */}
          {isLocked ? (
            <div style={styles.statusRow}>
              {exerciseStatus?.status === 'done' && (
                <div style={{ ...styles.statusBtn, ...styles.statusBtnDone, cursor: 'default' }}>
                  ✓ Completed
                </div>
              )}
              {exerciseStatus?.status === 'skipped' && (
                <div style={{ ...styles.statusBtn, ...styles.statusBtnSkipped, cursor: 'default' }}>
                  ✕ Skipped
                </div>
              )}
              {!exerciseStatus?.status && (
                <div style={{ ...styles.statusBtn, cursor: 'default' }}>
                  Session Complete
                </div>
              )}
            </div>
          ) : (
            <div style={styles.statusRow}>
              {/* Primary action — Done */}
              <button
                onClick={handleMarkDone}
                style={{
                  ...styles.statusBtn,
                  ...(exerciseStatus?.status === 'done' ? styles.statusBtnDone : styles.statusBtnPrimary),
                }}
              >
                {exerciseStatus?.status === 'done' ? '✓ Done' : 'Mark exercise done'}
              </button>
              {secondaryActions}
              {/* Secondary action — Skip as text link */}
              {exerciseStatus?.status === 'skipped' ? (
                <button
                  onClick={handleSkip}
                  style={{ ...styles.statusBtn, ...styles.statusBtnSkipped }}
                >
                  ✕ Skipped
                </button>
              ) : (
                <button
                  onClick={handleSkip}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: brandColors.silver,
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    padding: '4px 0',
                    textAlign: 'center',
                    textDecoration: 'underline',
                    textDecorationColor: 'rgba(142,142,147,0.4)',
                    textUnderlineOffset: '3px',
                  }}
                >
                  Skip this exercise
                </button>
              )}
            </div>
          )}

          {/* When the session is locked there is no Mark done above, so the tutorial link sits here. */}
          {isLocked && secondaryActions && <div style={{ marginTop: '8px' }}>{secondaryActions}</div>}

          {/* Skip Reason Input */}
          {showSkipReasons && (
            <div style={styles.skipReasonContainer}>
              <p style={styles.skipReasonLabel}>Why are you skipping? <span style={styles.required}>*</span></p>
              <input
                type="text"
                placeholder="Enter reason (required)"
                value={skipReasonText}
                onChange={(e) => setSkipReasonText(e.target.value)}
                style={styles.skipReasonInput}
                maxLength={100}
                autoFocus
              />
              <div style={styles.quickReasons}>
                {skippedReasons.map((reason) => (
                  <button
                    key={reason.value}
                    onClick={() => handleQuickSkipReason(reason.label)}
                    style={{
                      ...styles.quickReasonBtn,
                      ...(skipReasonText === reason.label ? styles.quickReasonBtnActive : {}),
                    }}
                  >
                    {reason.label}
                  </button>
                ))}
              </div>
              {/* Notify coach toggle */}
              <button
                onClick={() => setNotifyCoach(n => !n)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 0',
                  marginBottom: 10,
                  width: '100%',
                }}
              >
                <span style={{
                  width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                  background: notifyCoach ? brandColors.danger : 'transparent',
                  border: `2px solid ${notifyCoach ? brandColors.danger : brandColors.silver}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {notifyCoach && <span style={{ color: theme.text, fontSize: 11, lineHeight: 1 }}>✓</span>}
                </span>
                <span style={{ color: brandColors.silver, fontSize: 13 }}>Notify coach about this exercise</span>
              </button>

              <button
                onClick={handleConfirmSkip}
                disabled={!skipReasonText.trim()}
                style={{
                  ...styles.confirmSkipBtn,
                  opacity: skipReasonText.trim() ? 1 : 0.5,
                  cursor: skipReasonText.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Confirm Skip
              </button>
            </div>
          )}

          {/* Skipped Reason Display */}
          {exerciseStatus?.status === 'skipped' && exerciseStatus.skippedReason && (
            <div style={styles.skippedNotice}>
              <span>Skipped: {exerciseStatus.skippedReason}</span>
              {exerciseFlag && (
                <span style={{ color: brandColors.danger, fontSize: 12, marginLeft: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                  🚩 Coach notified
                  <button
                    onClick={handleRemoveFlag}
                    title="Remove notification"
                    style={{ background: 'none', border: 'none', color: brandColors.silver, cursor: 'pointer', fontSize: 12, padding: '0 2px', lineHeight: 1 }}
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Exercise History Modal */}
      {showHistoryModal && (
        <ExerciseHistoryModal
          exerciseId={resolvedExercise?.id || programExercise.exercise_id}
          exerciseName={exerciseName}
          userId={userId}
          isAssisted={!!resolvedExercise?.is_assisted}
          isBodyweight={!!resolvedExercise?.is_bodyweight}
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      {/* Session Swap Modal */}
      {showSwapModal && (
        <ExerciseSwapModal
          programExercise={programExercise}
          userId={userId}
          currentSwap={sessionSwap}
          onSwapApplied={(swap) => { setSessionSwap(swap); syncSwapToStorage(swap); }}
          onClose={() => setShowSwapModal(false)}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '16px',
    marginBottom: '12px',
    overflowX: 'hidden',
  },
  loadingText: {
    color: brandColors.silver,
    fontSize: '14px',
  },
  mainRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'stretch',
  },
  demoUnavailable: {
    cursor: 'default',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: brandColors.silver,
    fontSize: '11px',
    textAlign: 'center',
    padding: '8px',
    boxSizing: 'border-box',
  },
  gifContainer: {
    width: '120px',
    height: '120px',
    flexShrink: 0,
    padding: 0,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: brandColors.darkGray,
    cursor: 'pointer',
    position: 'relative',
  },
  gif: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  expandHint: {
    position: 'absolute',
    bottom: '2px',
    right: '2px',
    width: '20px',
    height: '20px',
    lineHeight: '20px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: brandColors.white,
    fontSize: '13px',
    textAlign: 'center',
  },
  secondaryRow: {
    display: 'flex',
    gap: '8px',
  },
  secondaryBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '11px 12px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '13px',
    fontWeight: '600',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  contentContainer: {
    flex: 1,
    minWidth: 0,
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    gap: '12px',
  },
  exerciseName: {
    color: brandColors.white,
    fontSize: '18px',
    fontWeight: '700',
  },
  target: {
    color: brandColors.silver,
    fontSize: '13px',
    backgroundColor: brandColors.darkGray,
    padding: '4px 10px',
    borderRadius: '4px',
  },
  setsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: 0,
    width: '100%',
  },
  setRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  setLabel: {
    color: brandColors.white,
    fontSize: '14px',
    fontWeight: '600',
    width: '44px',
    flexShrink: 0,
  },
  input: {
    backgroundColor: brandColors.darkGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    padding: '12px 10px',
    color: brandColors.white,
    fontSize: '16px', // 16px prevents iOS auto-zoom on focus
    width: '60px',
    outline: 'none',
    textAlign: 'center',
    minHeight: '44px',
  },
  // The weight and reps boxes of a set: each takes half the row.
  inputWide: {
    backgroundColor: brandColors.darkGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    padding: '12px 8px',
    color: brandColors.white,
    fontSize: '16px', // 16px prevents iOS auto-zoom on focus
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    textAlign: 'center',
    minHeight: '44px',
  },
  x: {
    color: brandColors.silver,
    fontSize: '14px',
  },
  checkBtn: {
    width: '44px',
    height: '44px',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: brandColors.lightGray,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  checkMark: {
    color: brandColors.darkGray,
    fontSize: '16px',
    fontWeight: 700,
  },
  lastWorkoutContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: brandColors.darkGray,
    borderRadius: '8px',
    gap: '12px',
  },
  lastWorkout: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  lastWorkoutLabel: {
    fontSize: '11px',
    color: brandColors.silver,
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  lastWorkoutValue: {
    fontSize: '13px',
    color: brandColors.white,
    fontWeight: '600',
  },
  lastWorkoutProgram: {
    fontSize: '11px',
    color: brandColors.gold,
    fontStyle: 'italic',
  },
  improvement: {
    fontSize: '11px',
    color: brandColors.gold,
    fontWeight: '600',
    marginTop: '2px',
  },
  historyBtn: {
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.silver,
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modalContent: {
    position: 'relative',
    maxWidth: '100%',
    maxHeight: '80vh',
  },
  expandedGif: {
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain',
    borderRadius: '14px',
  },
  closeBtn: {
    position: 'absolute',
    top: '-40px',
    right: '0',
    backgroundColor: 'transparent',
    border: 'none',
    color: brandColors.white,
    fontSize: '24px',
    cursor: 'pointer',
    padding: '8px',
  },
  // Status row (Done / Skip)
  statusRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  },
  statusBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '13px 14px',
    backgroundColor: brandColors.darkGray,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: brandColors.lightGray,
    borderRadius: '8px',
    color: brandColors.silver,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  // The one gold button on the card.
  statusBtnPrimary: {
    backgroundColor: theme.gold,
    borderColor: theme.gold,
    color: theme.bg,
    fontSize: '15px',
    fontWeight: '700',
  },
  statusBtnDone: {
    backgroundColor: theme.success,
    borderColor: theme.success,
    color: theme.bg,
  },
  statusBtnSkipped: {
    backgroundColor: 'rgba(255,149,0,0.15)',
    borderColor: 'rgba(255,149,0,0.4)',
    color: theme.warning,
  },
  // Skip reason container
  skipReasonContainer: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: brandColors.darkGray,
    borderRadius: '8px',
    border: `1px solid ${brandColors.lightGray}`,
  },
  skipReasonLabel: {
    margin: '0 0 10px 0',
    color: brandColors.silver,
    fontSize: '12px',
    fontWeight: '600',
  },
  required: {
    color: theme.danger,
  },
  skipReasonInput: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: brandColors.mediumGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '10px',
  },
  quickReasons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '12px',
  },
  quickReasonBtn: {
    padding: '6px 10px',
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '4px',
    color: brandColors.silver,
    fontSize: '11px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  quickReasonBtnActive: {
    backgroundColor: brandColors.gold,
    borderColor: brandColors.gold,
    color: brandColors.darkGray,
  },
  confirmSkipBtn: {
    width: '100%',
    padding: '10px 14px',
    backgroundColor: brandColors.lightGray,
    border: 'none',
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  // Skipped notice
  skippedNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
    padding: '10px 14px',
    backgroundColor: 'rgba(142, 142, 147, 0.2)',
    borderRadius: '8px',
    color: brandColors.silver,
    fontSize: '13px',
  },
};

export default WorkoutLogger;
